import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wordmark } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";
import { useT } from "@/lib/i18n";

function googleLoginRedirect() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginEmail, user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useT();

  useEffect(() => {
    if (!loading && user) navigate(user.role === "member" ? "/kentta" : "/app", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (params.get("expired") === "1") {
      toast.info(t("login.expired", "Istuntosi vanheni. Kirjaudu sisään uudelleen."), { duration: 5000 });
    }
  }, [params, t]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginEmail(email, password);
      track("Login Success", { method: "email" });
      toast.success(t("login.welcomeBack", "Tervetuloa takaisin!"));
      navigate(u?.role === "member" ? "/kentta" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login.failed", "Kirjautuminen epäonnistui"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left brand panel — wordmark + side-promo title, no image logo */}
      <div className="relative hidden md:flex flex-col p-12 brand-gradient text-white">
        <Link to="/" className="inline-flex items-center" data-testid="login-brand-link">
          <span className="font-display font-bold tracking-tight text-2xl inline-flex items-baseline gap-1">
            <span className="text-white">Veraso</span>
            <span className="text-emerald-300">Reports</span>
          </span>
        </Link>

        <div className="mt-8 max-w-md">
          <h2 className="font-display text-4xl font-semibold leading-tight">
            {t("login.sidePromoTitle")}
          </h2>
        </div>

        <div className="mt-auto text-xs uppercase tracking-widest font-semibold opacity-70">
          {t("login.tagline", "Kenttäraportointi puhelimella.")}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden mb-8 inline-flex" data-testid="login-mobile-brand-link">
            <Wordmark size="lg" />
          </Link>
          <h1 className="font-display text-3xl font-semibold text-slate-900">{t("login.title")}</h1>
          <p className="mt-2 text-slate-600">{t("login.noAccount")} <Link to="/register" className="text-emerald-600 font-semibold hover:underline">{t("login.signUp")}</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("login.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base"
                placeholder="name@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-700">{t("login.password")}</label>
                <Link to="/unohdin-salasanan" className="text-xs font-semibold text-emerald-600 hover:underline" data-testid="login-forgot-password-link">
                  Unohditko salasanasi?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full py-3.5"
              data-testid="login-submit-button"
            >
              {busy ? t("login.submitting") : t("login.submit")}
              {!busy && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" />
            {t("login.or", "TAI")}
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={() => { track("Login Click", { method: "google" }); googleLoginRedirect(); }}
            className="btn-ghost w-full py-3.5"
            data-testid="google-login-button"
          >
            <img src="/brand/google-icon.svg" alt="" className="h-5" />
            {t("login.google")}
          </button>
        </div>
      </div>
    </div>
  );
}
